/**
 * Pre-checkout email gate for VIP dual / video membership funnels.
 * Collects email before Stripe session create so premium_checkout_intents
 * + capture-checkout-email get a real address immediately (not "pending").
 */

import { Mail, X } from "lucide-react";
import { useEffect, useId, useState, type FC } from "react";
import {
  isValidCheckoutEmail,
  persistPreCheckoutEmail,
  trackCheckoutEmailModalPostHog,
} from "@/lib/preCheckoutEmail";

export type CheckoutEmailCaptureModalProps = {
  open: boolean;
  /** Prefill (Clerk / sessionStorage). */
  initialEmail?: string;
  /** es | en — copy */
  lang?: string;
  /** PostHog surface e.g. DualCTAVideoLanding */
  surface?: string;
  template?: string;
  loading?: boolean;
  /** Allow Stripe without email (Stripe collects later; recovery via cron). */
  allowSkip?: boolean;
  onSubmit: (email: string) => void;
  onSkip?: () => void;
  onClose: (kind: "backdrop" | "x_button" | "cancel_button") => void;
};

function copy(lang: string) {
  const es = lang.toLowerCase().startsWith("es");
  if (es) {
    return {
      title: "Tu email para continuar",
      subtitle:
        "Te enviamos el acceso y el link de pago. Así no perdés tu lugar si cerrás Stripe.",
      placeholder: "tu@email.com",
      continue: "Continuar al pago",
      skip: "Continuar sin email",
      cancel: "Cancelar",
      invalid: "Ingresá un email válido",
    };
  }
  return {
    title: "Your email to continue",
    subtitle:
      "We'll send your access and payment link — so you don't lose your spot if you leave Stripe.",
    placeholder: "you@email.com",
    continue: "Continue to payment",
    skip: "Continue without email",
    cancel: "Cancel",
    invalid: "Enter a valid email",
  };
}

export const CheckoutEmailCaptureModal: FC<CheckoutEmailCaptureModalProps> = ({
  open,
  initialEmail = "",
  lang = "en",
  surface = "vip_checkout",
  template,
  loading = false,
  allowSkip = true,
  onSubmit,
  onSkip,
  onClose,
}) => {
  const titleId = useId();
  const inputId = useId();
  const L = copy(lang);
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setEmail(initialEmail || "");
    setError(null);
    trackCheckoutEmailModalPostHog("opened", { surface, template });
  }, [open, initialEmail, surface, template]);

  if (!open) return null;

  const handleSubmit = () => {
    const trimmed = email.trim().toLowerCase();
    if (!isValidCheckoutEmail(trimmed)) {
      setError(L.invalid);
      trackCheckoutEmailModalPostHog("validation_error", {
        surface,
        template,
        has_email: Boolean(trimmed),
      });
      return;
    }
    persistPreCheckoutEmail(trimmed);
    trackCheckoutEmailModalPostHog("submitted", {
      surface,
      template,
      has_email: true,
    });
    onSubmit(trimmed);
  };

  const handleClose = (kind: "backdrop" | "x_button" | "cancel_button") => {
    trackCheckoutEmailModalPostHog("closed", {
      surface,
      template,
      close_kind: kind,
    });
    onClose(kind);
  };

  const handleSkip = () => {
    trackCheckoutEmailModalPostHog("skipped", { surface, template });
    onSkip?.();
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85"
      style={{ WebkitOverflowScrolling: "touch", isolation: "isolate" }}
      onClick={() => handleClose("backdrop")}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        className="absolute inset-0 backdrop-blur-sm"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}
        aria-hidden
      />
      <div
        className="relative mx-4 w-full sm:mx-auto"
        style={{
          maxWidth: "min(90vw, 420px)",
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="w-full overflow-hidden rounded-2xl shadow-2xl sm:rounded-3xl"
          style={{
            background: "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow:
              "0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.05)",
          }}
        >
          <div className="relative px-5 pb-4 pt-5 sm:px-6 sm:pt-6">
            <button
              type="button"
              onClick={() => handleClose("x_button")}
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-all hover:bg-white/10 hover:text-white"
              style={{ WebkitTapHighlightColor: "transparent" }}
              aria-label={L.cancel}
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex flex-col items-center pr-8 text-center">
              <div
                className="mb-3 flex h-14 w-14 items-center justify-center rounded-full sm:mb-4 sm:h-16 sm:w-16"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(56, 189, 248, 0.15) 0%, rgba(14, 165, 233, 0.25) 100%)",
                  border: "1px solid rgba(56, 189, 248, 0.2)",
                }}
              >
                <Mail className="h-7 w-7 text-sky-400 sm:h-8 sm:w-8" />
              </div>
              <h3
                id={titleId}
                className="mb-2 font-body text-xl font-bold text-white sm:text-2xl"
              >
                {L.title}
              </h3>
              <p className="max-w-sm text-sm leading-relaxed text-gray-400 sm:text-base">
                {L.subtitle}
              </p>
            </div>
          </div>

          <div className="px-5 pb-5 sm:px-6 sm:pb-6">
            <div className="mb-5">
              <label htmlFor={inputId} className="sr-only">
                Email
              </label>
              <input
                id={inputId}
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                disabled={loading}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && email.trim()) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder={L.placeholder}
                className="w-full rounded-xl px-4 py-3.5 text-base text-white placeholder-gray-500 transition-all sm:rounded-2xl sm:py-4 sm:text-lg"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: error
                    ? "2px solid rgba(239, 68, 68, 0.5)"
                    : "2px solid rgba(255, 255, 255, 0.1)",
                  fontSize: "16px",
                  WebkitAppearance: "none",
                  appearance: "none",
                }}
                autoFocus
              />
              {error && (
                <p className="mt-2.5 px-1 text-sm text-red-400" role="alert">
                  {error}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => handleClose("cancel_button")}
                disabled={loading}
                className="order-2 min-h-[48px] rounded-xl px-5 py-3.5 text-base font-semibold text-white transition-all sm:order-1 sm:flex-1 sm:rounded-2xl"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.06)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {L.cancel}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !email.trim()}
                className="order-1 min-h-[48px] rounded-xl px-5 py-3.5 text-base font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-50 sm:order-2 sm:flex-1 sm:rounded-2xl"
                style={{
                  background:
                    "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {loading ? "…" : L.continue}
              </button>
            </div>

            {allowSkip && onSkip && (
              <button
                type="button"
                onClick={handleSkip}
                disabled={loading}
                className="mt-3 w-full py-2 text-center text-xs text-gray-500 underline-offset-2 transition-colors hover:text-gray-300 hover:underline"
              >
                {L.skip}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutEmailCaptureModal;
